import React, { useMemo } from "react";

import LocationCityIcon from "@mui/icons-material/LocationCity";
import { Stack, Typography } from "@mui/material";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { DealBase } from "store/types";

import { Breadcrumbs } from "./Metadata/Breadcrubms";
import { useWorkspace } from "./WorkspaceContext";

const tableBodyRowProps = {
  sx: {
    backgroundColor: "white",
    height: "32px",
    minHeight: "32px",
    "& td": { verticalAlign: "middle", height: "32px", minHeight: "32px" },
    "&:hover td": { backgroundColor: "#ecf1f8" },
    "&:last-child td": { borderBottom: "none" }
  }
};

export const Deals: React.FC = () => {
  const { deals } = useWorkspace();
  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<DealBase>[]>(
    () => [
      {
        id: "title",
        header: "Deal Name",
        accessorKey: "title",
        // size: 240,
        // minSize: 180,
        Cell: ({ cell }) => (
          <Typography
            noWrap
            sx={{
              fontSize: 14,
              color: "#071f35",
              lineHeight: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        )
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    data: deals,
    columns,
    getRowId: (row) => String(row.id),
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTopToolbar: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableRowVirtualization: true,
    enableColumnActions: false,
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #dbe8f5",
        borderRadius: "8px",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minHeight: 0,
        overflow: "auto"
      }
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f6f8fc",
        color: "#071f35",
        fontSize: 14,
        fontWeight: 500,
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "4px",
        px: "16px",
        borderBottom: "1px solid #dbe8f5"
      }
    },
    muiTableHeadRowProps: {
      sx: {
        height: "32px",
        minHeight: "32px",
        "& th": { height: "32px", minHeight: "32px" }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: "white",
        fontSize: 14,
        color: "#071f35",
        borderBottom: "1px solid #dbe8f5",
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "8px",
        px: "16px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden"
      }
    },
    muiTableBodyRowProps: ({ row }) => ({
      ...tableBodyRowProps,
      onClick: () => navigate({ route: "workspaceDeal", params: { deal_id: row.original.id } }),
      sx: { ...tableBodyRowProps.sx, cursor: "pointer" }
    })
  });

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex"
      }}
      gap="16px"
    >
      <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          links={[
            { path: Routes.workspaceDeals, title: "Workspace" },
            { path: Routes.workspaceDeals, title: "Deals" }
          ]}
        />

        {/* Icon + Title */}
        <Stack direction="row" alignItems="center" gap="10px" sx={{ width: "100%" }}>
          <LocationCityIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
          <Typography
            noWrap
            sx={{
              color: "#0a3142",
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "24px"
            }}
          >
            Deals
          </Typography>
        </Stack>
      </Stack>

      {/* Table area: takes remaining height and scrolls */}
      <Stack
        sx={{
          overflow: "hidden",
          flexGrow: 1
        }}
      >
        <MaterialReactTable table={table} />
      </Stack>
    </Stack>
  );
};
